import {
  ColorVariant,
  ComponentAlignment,
  MapComponent,
  MapMarker,
  MarkerVariant,
  MaterialIcon,
  RawSpacingInput,
} from '@makyoapp/makyo-tw-ui'

import { GOOGLE_MAP_API_KEY } from '@/shared/gapi'
import { cleanUpText, generateMarkersHash } from '@/shared/utils'

import { CompLocationSettings } from '../location/types'

const zoomMapping: Record<string, number> = {
  far: 5,
  medium: 10,
  near: 15,
}

export interface CompLocationMultiProps {
  margin: RawSpacingInput
  alignment: string
  previewWidth: string
  previewHeight: string
  defaultMarkerSettings: CompLocationSettings
  remainingMarkers: CompLocationSettings[]
  markerItems: CompLocationSettings[]
  mapSettings: Record<string, any>
  zoom: number
  mode: 'dark' | 'light'
  colorVariant: string
  colorMarker: string
  simulateAction: any
}

/**
 * CompLocationMultiCore
 */
export const CompLocationMultiCore = ({
  alignment,
  margin,
  previewWidth,
  previewHeight,
  defaultMarkerSettings,
  remainingMarkers,
  markerItems,
  mapSettings,
  zoom,
  mode,
  colorVariant,
  simulateAction,
  colorMarker,
}: CompLocationMultiProps) => {
  const zoomLevel = zoomMapping[mapSettings.zoomLevel] || zoom || 15

  const getCustomElement = (settings: CompLocationSettings) => {
    if (settings.icon) {
      return (
        <div
          className={`bg-${colorMarker} rounded-full shadow-lg flex items-center justify-center cursor-pointer text-on-${colorMarker} p-2`}
        >
          <MaterialIcon icon={settings.icon} size={36} />
        </div>
      )
    }

    if (settings.image) {
      const MarkersIcon = () => (
        <svg width='32' height='40' viewBox='0 0 32 40' xmlns='http://www.w3.org/2000/svg'>
          <path d='M16.0044 19.5C16.9681 19.5 17.7917 19.1569 18.475 18.4706C19.1583 17.7843 19.5 16.9593 19.5 15.9956C19.5 15.0319 19.1569 14.2083 18.4706 13.525C17.7843 12.8417 16.9593 12.5 15.9956 12.5C15.0319 12.5 14.2083 12.8431 13.525 13.5294C12.8417 14.2157 12.5 15.0407 12.5 16.0044C12.5 16.9681 12.8431 17.7917 13.5294 18.475C14.2157 19.1583 15.0407 19.5 16.0044 19.5ZM16 40C10.6333 35.4333 6.625 31.1917 3.975 27.275C1.325 23.3583 0 19.7333 0 16.4C0 11.4 1.60833 7.41667 4.825 4.45C8.04167 1.48333 11.7667 0 16 0C20.2333 0 23.9583 1.48333 27.175 4.45C30.3917 7.41667 32 11.4 32 16.4C32 19.7333 30.675 23.3583 28.025 27.275C25.375 31.1917 21.3667 35.4333 16 40Z'></path>
        </svg>
      )

      const Image = () => (
        <div
          className={`w-9 h-9 rounded-full shadow-lg flex items-center justify-center cursor-pointer`}
          style={{ color: `var(--color-${colorMarker})` }}
        >
          <MarkersIcon />
          <div className='absolute top-0.5 left-1/2 transform -translate-x-1/2 '>
            <img src={settings.image} className='w-6 h-6 rounded-full' />
          </div>
        </div>
      )

      return <Image />
    }

    return undefined
  }

  const handleItemClick = (settings: CompLocationSettings) => {
    const onItemClickActions = settings.clickEvents
      ? Array.isArray(settings.clickEvents)
        ? settings.clickEvents
        : [settings.clickEvents]
      : []

    if (onItemClickActions?.length > 0) {
      simulateAction({
        actions: onItemClickActions,
        tableId: settings.tableId,
        rowId: settings.rowId,
      })
    }
  }

  const createMarker = (settings: CompLocationSettings) => {
    if (!settings) return null
    const { latitude, longitude, title, subtitle, description, image, avatar } = settings

    if (!latitude || !longitude) return null

    const customElement = getCustomElement(settings)

    return {
      position: { lat: latitude, lng: longitude },
      info: {
        variant: settings.info || ('default' as MarkerVariant),
        header: cleanUpText(title || ''),
        subHeader: cleanUpText(subtitle || ''),
        description: cleanUpText(description || ''),
        ...(image && { image: { src: image, alt: title } }),
        ...(avatar && { avatar: { src: avatar, alt: title } }),
      },
      onPopoverClick: () => handleItemClick(settings),
      ...(customElement && { customElement }),
    }
  }

  const allMarkers: MapMarker[] = [defaultMarkerSettings, ...(remainingMarkers || [])]
    .map(createMarker)
    .filter(Boolean) as MapMarker[]

  return (
    <MapComponent
      margin={margin}
      width={previewWidth}
      height={previewHeight}
      cornerRadius={mapSettings.shape}
      alignment={alignment as ComponentAlignment}
      key={[
        generateMarkersHash(markerItems),
        zoomLevel,
        mapSettings.alignment,
        mapSettings.fullscreenControl,
        mapSettings.streetViewControl,
        mapSettings.mapTypeControl,
        mapSettings.zoomControl,
        mapSettings.clustered,
        colorMarker,
        colorVariant,
      ].join('-')}
      colorMarker={colorMarker as ColorVariant}
      apiKey={GOOGLE_MAP_API_KEY}
      colorVariant={colorVariant as ColorVariant}
      mapTypeControl={mapSettings.mapTypeControl}
      zoomControl={mapSettings.zoomControl}
      streetViewControl={mapSettings.streetViewControl}
      fullscreenControl={mapSettings.fullscreenControl}
      zoom={zoomLevel}
      clustered={mapSettings.clustered}
      theme={mode}
      markers={allMarkers}
    />
  )
}
